import type {
  Activity,
  BatchArgs,
  BatchResponse,
  IDemo,
  IPresentation,
  IRecording,
} from '@carboncopyco/types';
import type { EventAction, EventTarget } from '@carboncopyco/types';
import { Debug, removeUndefinedValues } from '@carboncopyco/utils';
import { handleBatchRequest } from '../utils/handleBatchRequest';
import { mainApi } from './mainApi';
import type { Tags } from './tagTypes';

type Tag = (typeof Tags)[number];

Debug.extend('demostack-data:api:eventApi');

export interface CreateEventArgs {
  organizationId: string;
  demoId?: string;
  presentationId?: string;
  demoName?: IDemo['demoName'];
  path?: string;
  action: EventAction;
  target: EventTarget;
  workspaceId?: string;
}

export interface GetEventsArgs extends BatchArgs {
  organizationId: string;
  workspaces?: string;
}

const eventApi = mainApi.injectEndpoints({
  endpoints: (builder) => ({
    postEvent: builder.mutation<void, CreateEventArgs>({
      query: ({
        organizationId,
        demoId,
        path,
        action,
        target,
        presentationId,
        demoName,
        workspaceId,
      }) => ({
        url: `/api/${organizationId}/events`,
        method: 'POST',
        body: {
          demoId,
          path,
          action,
          target,
          presentationId,
          demoName,
          workspaceId,
        },
      }),
      invalidatesTags: (_, __, { presentationId, demoId }) => {
        const tags: { type: Tag; id?: string }[] = [{ type: 'Events' }];
        if (presentationId) {
          tags.push({
            type: 'Presentation',
            id: presentationId,
          });
        }
        if (demoId) {
          tags.push({ type: 'Demo', id: demoId });
        }
        return tags;
      },
    }),

    getEvents: builder.query<
      BatchResponse<Activity<IDemo | IRecording | IPresentation>>,
      GetEventsArgs
    >({
      query: ({ organizationId, workspaces = 'all', ...rest }) =>
        handleBatchRequest({
          ...rest,
          params: removeUndefinedValues({ workspaces }),
          url: `/api/${organizationId}/events`,
        }),
      providesTags: ['Events'],
    }),
  }),
});

export const {
  usePostEventMutation,
  useGetEventsQuery,
  useLazyGetEventsQuery,
} = eventApi;
